import { nextSlotFor } from "./programSchedule.js";
import type { Locale } from "./locale.js";
import {
  resolveProgramSession,
  type ResolvedSession,
} from "./programResolution.js";
import type {
  Program as ProgramT,
  ProgramProgress as ProgramProgressT,
} from "./schemas/program.js";

// Pure selection of the Home hero's "today's session": from the athlete's
// FOLLOWED programs (status=active — drafts and published-not-followed are
// never startable, Follow+Publish model), pick the one they're actively
// running (most recently progressed) and resolve its next slot via the same
// auto-advance math the Programs section uses. Returns null when there's no
// followed program with a resolvable session — the hero renders blank. Lifted
// from apps/mobile with the Home hero surface so mobile + the web-rnw console
// resolve today's session the same way.

export type TodaySession = {
  program: ProgramT;
  weekIdx: number;
  sessionIdx: number;
  resolved: ResolvedSession;
};

// Order followed programs most-recently-progressed FIRST, so the active
// cycle leads (the Plan highlight carousel renders them in this order, and
// pickTodaySession picks the head). Programs with no progress sort last
// (empty lastCompletedAt) but stay eligible. Pure + stable (ties keep input
// order). Shared by pickTodaySession + the carousel so both agree on which
// program is "card 0".
export function orderFollowedByRecency(
  followedPrograms: ReadonlyArray<ProgramT>,
  progress: Record<string, ProgramProgressT | null> | null | undefined,
): ProgramT[] {
  return [...followedPrograms].sort((a, b) => {
    const ap = progress?.[a.id]?.lastCompletedAt ?? "";
    const bp = progress?.[b.id]?.lastCompletedAt ?? "";
    return bp.localeCompare(ap);
  });
}

export function pickTodaySession(
  followedPrograms: ReadonlyArray<ProgramT>,
  progress: Record<string, ProgramProgressT | null> | null | undefined,
  // PE-21 — reader's language for the program's authored text.
  locale?: Locale,
): TodaySession | null {
  if (followedPrograms.length === 0) return null;
  const ordered = orderFollowedByRecency(followedPrograms, progress);
  for (const program of ordered) {
    const slot = nextSlotFor(program, progress?.[program.id] ?? null);
    const resolved = resolveProgramSession({
      program,
      weekIdx: slot.weekIdx,
      sessionIdx: slot.sessionIdx,
      locale,
    });
    if (resolved) {
      return {
        program,
        weekIdx: slot.weekIdx,
        sessionIdx: slot.sessionIdx,
        resolved,
      };
    }
  }
  return null;
}
